import api from "@/lib/axiosConfig";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

const monetaryTransactionSchema = z.object({
  id: z.string().cuid(),
  category: z.string(),
  amount: z.number().int(), // in cents
  currency: z.string(),
  title: z.string(),
  date: z.string().nonempty(),
  updatedAt: z.date().optional(),
  createdAt: z.date().optional(),
  relatedArtworkId: z.string(),
  instanceId: z.string(),
});

export type MonetaryTransaction = z.infer<typeof monetaryTransactionSchema>;

const _addMonetaryTransactionSchema = monetaryTransactionSchema
  .pick({
    relatedArtworkId: true,
    title: true,
    date: true,
    amount: true,
    currency: true,
  })
  .required();

export type AddMonetaryTransaction = z.infer<
  typeof _addMonetaryTransactionSchema
>;

const _briefMonetaryTransactionSchema = monetaryTransactionSchema.pick({
  id: true,
  title: true,
  date: true,
  amount: true,
  category: true,
  currency: true,
});

export type BriefMonetaryTransaction = z.infer<
  typeof _briefMonetaryTransactionSchema
>;

const BASE_ROUTE = "back/api/artworks/monetary-transaction";

async function addMonetaryTransaction({
  category,
  transaction,
}: {
  category: string;
  transaction: AddMonetaryTransaction;
}) {
  const { data } = await api.put(`${BASE_ROUTE}/${category}`, transaction);

  return data;
}

export function useAddMonetaryTransaction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addMonetaryTransaction,
    onSuccess: (_, { category }) => {
      queryClient.invalidateQueries({
        queryKey: ["monetaryTransactions", category],
      });
    },
  });
}

async function getMonetaryTransactionByCategory(
  artworkId: string,
  category: string,
) {
  const { data } = await api.get<BriefMonetaryTransaction[]>(
    `${BASE_ROUTE}/${artworkId}/${category}`,
  );
  return data;
}

export function useGetMonetaryTransactionByCategory(
  artworkId: string,
  category: string,
) {
  return useQuery({
    queryFn: () => getMonetaryTransactionByCategory(artworkId, category),
    queryKey: ["monetaryTransactions", category],
  });
}

async function deleteMonetaryTransaction({
  id,
}: Pick<MonetaryTransaction, "id" | "category">) {
  await api.delete(`${BASE_ROUTE}/${id}`);
}

export function useDeleteMonetaryTransaction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteMonetaryTransaction,
    onSuccess: (_, { id, category }) => {
      queryClient.invalidateQueries({
        queryKey: ["monetaryTransactions", category],
      });
      queryClient.invalidateQueries({
        queryKey: ["monetaryTransaction", id],
      });
    },
  });
}
